import { createContext, useContext, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { trackTool, withTool } from "../../lib/track";

export const ToolContext = createContext({ tool: null, onApplySize: null, embed: false });
export const useTool = () => useContext(ToolContext);

export const field = "bg-black/30 border border-white/15 rounded-sm px-2.5 py-2 text-sm text-white font-mono outline-none focus:border-[#B87333] w-full";
export const label = "block font-mono text-[10px] tracking-[0.2em] uppercase text-white/60 mb-1";
export const fmt = (n, d = 2) => (Number.isFinite(n) ? n.toFixed(d).replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "") : "—");

export const Panel = ({ icon: Icon, title, children, testId }) => {
  const outer = useTool();
  useEffect(() => { trackTool(testId, "view"); }, [testId]);
  return (
    <ToolContext.Provider value={{ ...outer, tool: testId }}>
      <div className="bg-[#1F2830] text-white rounded-sm p-5 md:p-6 my-4 not-prose" data-testid={testId}>
        <div className="flex items-center gap-2 mb-4 font-mono text-[11px] tracking-[0.24em] uppercase font-bold">
          {Icon && <Icon className="w-4 h-4 text-[#B87333]" />} {title}
        </div>
        {children}
      </div>
    </ToolContext.Provider>
  );
};

export const Result = ({ title, value, sub, testId, tone }) => (
  <div className={`rounded-sm p-3 ${tone === "bad" ? "bg-red-950/60 border border-red-500/40" : tone === "warn" ? "bg-amber-950/50 border border-amber-500/40" : tone === "good" ? "bg-emerald-950/50 border border-emerald-500/40" : "bg-black/30"}`}>
    <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/60">{title}</div>
    <div className="font-display font-black text-xl text-[#E8B587] break-words" data-testid={testId}>{value}</div>
    {sub && <div className="text-[11px] text-white/60 mt-1" data-testid={testId ? `${testId}-sub` : undefined}>{sub}</div>}
  </div>
);

export const Cta = ({ to, children, testId, outline }) => {
  const { tool, embed } = useTool();
  const href = withTool(to, tool);
  const cls = `inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase font-bold px-4 py-2.5 transition-colors ${outline ? "border border-white/25 hover:border-[#B87333] text-white" : "bg-[#DD6B33] hover:bg-[#c85d28] text-white"}`;
  const onClick = () => trackTool(tool, embed ? "embed_cta" : "cta", { to });
  if (typeof to === "string" && to.startsWith("#")) {
    if (embed) return null;
    return <a href={to} className={cls} data-testid={testId} onClick={onClick}>{children} <ArrowUpRight className="w-3.5 h-3.5" /></a>;
  }
  if (embed) return <a href={`${window.location.origin}${href}`} target="_blank" rel="noreferrer" className={cls} data-testid={testId} onClick={onClick}>{children} <ArrowUpRight className="w-3.5 h-3.5" /></a>;
  return <Link to={href} className={cls} data-testid={testId} onClick={onClick}>{children} <ArrowUpRight className="w-3.5 h-3.5" /></Link>;
};

// "Use this size" — applies directly when embedded in a configurator, otherwise jumps to the product with ?w=&h=.
export const ApplySize = ({ w, h, productSlug, testId, children }) => {
  const { tool, onApplySize, embed } = useTool();
  const cls = "inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase font-bold px-4 py-2.5 bg-[#B87333] hover:bg-[#a0622a] text-white transition-colors";
  const track = () => trackTool(tool, "apply_size", { w, h, productSlug, embed: !!embed });
  if (onApplySize) return <button type="button" onClick={() => { track(); onApplySize(w, h); }} className={cls} data-testid={testId}>{children} <ArrowUpRight className="w-3.5 h-3.5" /></button>;
  const href = withTool(`/shop/product/${productSlug}?w=${fmt(w)}&h=${fmt(h)}`, tool);
  if (embed) return <a href={`${window.location.origin}${href}`} target="_blank" rel="noreferrer" onClick={track} className={cls} data-testid={testId}>{children} <ArrowUpRight className="w-3.5 h-3.5" /></a>;
  return <Link to={href} onClick={track} className={cls} data-testid={testId}>{children} <ArrowUpRight className="w-3.5 h-3.5" /></Link>;
};

export const Segmented = ({ value, onChange, options, testId }) => (
  <div className="flex flex-wrap" data-testid={testId}>
    {options.map((o) => (Array.isArray(o) ? { value: o[0], label: o[1] } : o)).map((o) => (
      <button key={o.value} type="button" onClick={() => onChange(o.value)} className={`flex-1 min-w-[4rem] py-2 px-2 font-mono text-xs border border-white/15 ${value === o.value ? "bg-[#B87333] text-white" : "bg-black/30 text-white/70 hover:text-white"}`} data-testid={testId ? `${testId}-${o.value}` : undefined}>{o.label}</button>
    ))}
  </div>
);
